import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Drawer, IconButton, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { IconX } from "@tabler/icons-react";

import { fetchCompanies, updateCompany } from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { AdminPage, AdminTable, RowButton } from "./AdminShell";
import tokens from "../../theme/tokens";

const { color, font, layout, radius, shadow } = tokens;

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { padding: "12px 13px", fontSize: 13.5 },
};

const Label = ({ children }) => (
  <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
    {children}
  </Box>
);

const EMPTY = { name: "", phone: "", admin_email: "", email_domain: "" };

/** Cross-company admin — every company on the platform. Super-admin only. */
const Companies = () => {
  const dispatch = useDispatch();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCompanies();
      setCompanies(res.companies || []);
    } catch (e) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: e.message || "Failed to load companies.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (company) => {
    setEditing(company);
    setForm({
      name: company.name || "",
      phone: company.phone || "",
      admin_email: company.admin_email || "",
      email_domain: company.email_domain || "",
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCompany(editing.id, form);
      dispatch(
        showAppAlert({ show: true, alertType: "success", alertMessage: "Company updated." })
      );
      setEditing(null);
      load();
    } catch (e) {
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: e.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPage
      title="Companies"
      subtitle="Every company on the platform."
    >
      <AdminTable
        loading={loading}
        empty="No companies found."
        rows={companies}
        columns={[
          { key: "name", label: "Name" },
          { key: "admin_email", label: "Admin email", width: "1.4fr" },
          { key: "email_domain", label: "Domain", width: "0.9fr" },
          { key: "phone", label: "Phone", width: "0.9fr" },
          {
            key: "actions",
            label: "",
            width: "70px",
            render: (c) => <RowButton onClick={() => openEdit(c)}>Edit</RowButton>,
          },
        ]}
      />

      <Drawer
        anchor="right"
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        PaperProps={{
          sx: {
            width: layout.detailPanel,
            maxWidth: "100%",
            boxShadow: shadow.panel,
            borderLeft: `1px solid ${color.border}`,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 2.75, pb: 2 }}>
          <Box sx={{ flex: 1, font: `600 15px ${font.sans}`, color: color.text }}>
            Edit company
          </Box>
          <IconButton size="small" onClick={() => setEditing(null)}>
            <IconX size={16} stroke={1.75} color={color.textMuted} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, pb: 2 }}>
          {[
            ["name", "Company name"],
            ["admin_email", "Admin email"],
            ["email_domain", "Email domain"],
            ["phone", "Phone"],
          ].map(([key, label]) => (
            <Box key={key} sx={{ mb: 2 }}>
              <Label>{label}</Label>
              <TextField
                fullWidth
                size="small"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                sx={field}
              />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.75,
            py: 2,
            borderTop: `1px solid ${color.border}`,
            bgcolor: color.bg,
          }}
        >
          <Button variant="outlined" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      </Drawer>
    </AdminPage>
  );
};

export default Companies;
